import { useEffect, useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { userApi } from '../../api'
import type { UserRelation } from '../../types/api'
import { useTheme } from '../../hooks/useTheme'
import { useTranslation } from '../../i18n/useTranslation'
import { useAuth } from '../../contexts/AuthContext'
import { Badge, ErrorMessage, Button } from '../ui'
import { translateUserRelationStatus } from '../../utils/statusTranslations'
import { UserRelationCard } from './UserRelationCard'

export default function MyCoaches() {
  const navigate = useNavigate()
  const theme = useTheme()
  const { t } = useTranslation()
  const { user } = useAuth()
  const showBecomeSpecialist = !user?.hasTrainees

  const [coaches, setCoaches] = useState<UserRelation[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadCoaches()
  }, [])

  async function loadCoaches() {
    setLoading(true)
    setError(null)
    try {
      const coachesList = await userApi.getCoaches()
      setCoaches(coachesList)
    } catch (err) {
      console.error('Error loading coaches:', err)
      setError(err instanceof Error ? err.message : 'Failed to load coaches')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="p-5">
      <header className="mb-6 flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl font-bold mb-1" style={{ color: theme.text.primary }}>
            {t.dashboard.myCoaches.title}
          </h1>
          <p className="text-sm" style={{ color: theme.text.muted }}>
            {t.dashboard.myCoaches.description}
          </p>
        </div>
        <Link
          to="/trainees"
          className="flex-shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium active:opacity-75 transition-opacity border"
          style={{
            backgroundColor: theme.cardBg,
            borderColor: theme.cardBorder,
            color: theme.text.primary,
          }}
        >
          {t.navigation.clients}
        </Link>
      </header>

      {loading ? (
        <div className="flex flex-col gap-3">
          {[1, 2].map(i => (
            <div
              key={i}
              className="rounded-2xl p-4 animate-pulse"
              style={{ backgroundColor: theme.cardBg, border: `1px solid ${theme.cardBorder}` }}
            >
              <div className="flex items-center gap-3">
                <div
                  className="w-14 h-14 rounded-full flex-shrink-0"
                  style={{ backgroundColor: `${theme.text.muted}25` }}
                />
                <div className="flex-1 space-y-2">
                  <div className="h-3 rounded-full w-28" style={{ backgroundColor: `${theme.text.muted}25` }} />
                  <div className="h-2.5 rounded-full w-20" style={{ backgroundColor: `${theme.text.muted}18` }} />
                  <div className="h-5 rounded-md w-16" style={{ backgroundColor: `${theme.text.muted}18` }} />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : error ? (
        <ErrorMessage
          title={t.myCoaches.error.title}
          message={error}
          icon="⚠️"
          onRetry={loadCoaches}
          retryLabel={t.myCoaches.error.tryAgain}
        />
      ) : coaches.length > 0 ? (
        <>
          <div className="flex flex-col gap-3">
            {coaches.map(coach => {
              const displayName = coach.firstName
                ? `${coach.firstName} ${coach.lastName || ''}`.trim()
                : coach.username || 'Unknown'

              return (
                <UserRelationCard
                  key={coach.id}
                  displayName={displayName}
                  username={coach.username}
                  photoUrl={coach.photoUrl}
                  fallbackBg={theme.brand.medium}
                  onClick={() => navigate(`/coach/${coach.id}`, { state: { coach } })}
                  badges={
                    <Badge
                      variant={coach.relationStatus === 'ACTIVE' ? 'success' : coach.relationStatus?.includes('PENDING') ? 'warning' : 'info'}
                      size="sm"
                      className="rounded-md flex-shrink-0"
                    >
                      {translateUserRelationStatus(coach.relationStatus, t)}
                    </Badge>
                  }
                />
              )
            })}
          </div>

          {showBecomeSpecialist && (
            <div className="mt-6">
              <Button
                type="button"
                variant="primary"
                size="md"
                fullWidth
                onClick={() => navigate('/become-specialist')}
              >
                {t.traineeDashboard.becomeCoach.title}
              </Button>
            </div>
          )}
        </>
      ) : (
        <div className="flex flex-col items-center py-14 text-center">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4" style={{ backgroundColor: `${theme.buttonColor}15` }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ color: theme.buttonColor }}>
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
              <circle cx="12" cy="7" r="4"/>
            </svg>
          </div>
          <h3 className="text-base font-semibold mb-1" style={{ color: theme.text.primary }}>
            {t.myCoaches.emptyState.title}
          </h3>
          <p className="text-sm px-8 mb-6" style={{ color: theme.text.muted }}>
            {t.myCoaches.emptyState.description}
          </p>
          {showBecomeSpecialist && (
            <Button
              type="button"
              variant="primary"
              size="lg"
              fullWidth
              onClick={() => navigate('/become-specialist')}
            >
              {t.traineeDashboard.becomeCoach.title}
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
